import React, { useState, useEffect } from 'react';
import { FaPen, FaChartLine, FaClipboardList, FaUsers, FaChartBar, FaUsersCog, FaRocket, FaArrowRight, FaBullseye, FaRegClock, FaExclamationTriangle, FaFileExcel, FaHistory, FaUser, FaExpand, FaFilter, FaBell, FaFileAlt, FaQuestionCircle, FaLightbulb } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { Student, Dimension as DimensionType } from '../types';
// Tutorial removido - ahora manejado por SimpleTutorialSection

// Interface for dimension categories passed from App.tsx (for UI structure)
interface DimensionCategory {
  name: string;
  color: string;
  dimensions: {
    id: number;
    name: string;
    component: React.FC<any>;
    icon: IconType;
  }[];
}

interface DashboardProps {
  username: string;
  role: 'admin' | 'teacher';
  allowedCourses: string[];
  students: Student[];
  dimensionCategories: DimensionCategory[];
  dimensionsTemplate: DimensionType[];
  onDimensionSelect: (dimensionId: number) => void;
  onSectionChange?: (section: 'dashboard' | 'formularios' | 'analytics' | 'usuarios' | 'exportacion') => void;
  onViewModeChange?: (mode: 'student' | 'skill') => void;
}

// Funciones de cálculo reutilizadas
const calculateStudentOverallProgress = (student: Student, dimensionsTemplate: DimensionType[]): number => {
  if (!student.skillLevels || student.skillLevels.length === 0 || !dimensionsTemplate || dimensionsTemplate.length === 0) return 0;

  let totalPercentageSum = 0;
  let dimensionsActuallyEvaluatedCount = 0;

  dimensionsTemplate.forEach(dimFromTemplate => {
    if (!dimFromTemplate.skills || dimFromTemplate.skills.length === 0) return;

    const skillsInThisDimensionTemplate = dimFromTemplate.skills;
    let achievedScoreInDimension = 0;

    const studentSkillsForThisDimension = student.skillLevels.filter(sl => sl.dimensionId === dimFromTemplate.id);

    if (studentSkillsForThisDimension.length > 0) {
      skillsInThisDimensionTemplate.forEach(templateSkill => {
        const studentSkillLevel = studentSkillsForThisDimension.find(sl => sl.skillId === templateSkill.id);
        if (studentSkillLevel && studentSkillLevel.level !== null) {
          achievedScoreInDimension += (studentSkillLevel.level + 1) * 20;
        }
      });

      if (skillsInThisDimensionTemplate.length > 0) {
        totalPercentageSum += (achievedScoreInDimension / skillsInThisDimensionTemplate.length);
        dimensionsActuallyEvaluatedCount++;
      }
    }
  });

  if (dimensionsActuallyEvaluatedCount === 0) return 0;
  return totalPercentageSum / dimensionsActuallyEvaluatedCount;
};

const calculateStudentDataCompleteness = (student: Student, dimensionsTemplate: DimensionType[]): number => {
  if (!dimensionsTemplate || dimensionsTemplate.length === 0) return 0;

  let totalSkillsInTemplate = 0;
  let skillsFilledByStudent = 0;

  dimensionsTemplate.forEach(dim => {
    if (!dim.skills) return;
    totalSkillsInTemplate += dim.skills.length;
    // ✅ CORREGIDO: Solo contar habilidades definidas en frontend que tengan nivel en BD
    dim.skills.forEach(skill => {
      const foundSkillLevel = student.skillLevels.find(sl => sl.dimensionId === dim.id && sl.skillId === skill.id);
      if (foundSkillLevel && foundSkillLevel.level !== null) {
        skillsFilledByStudent++;
      }
    });
  });

  if (totalSkillsInTemplate === 0) return 0;
  return (skillsFilledByStudent / totalSkillsInTemplate) * 100;
};

const formatCurrentDate = (): string => {
  const today = new Date();
  const options: Intl.DateTimeFormatOptions = { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return today.toLocaleDateString('es-ES', options);
};

const Dashboard: React.FC<DashboardProps> = ({ username, role, students, dimensionsTemplate, onSectionChange, onDimensionSelect, onViewModeChange }) => {
  const totalStudents = students.length;

  // Tutorial ahora manejado por SimpleTutorialSection

  // Calcular estadísticas básicas
  const overallAverageProgress = React.useMemo(() => {
    if (totalStudents === 0) return 0;
    const totalProgressSum = students.reduce((sum, student) => sum + calculateStudentOverallProgress(student, dimensionsTemplate), 0);
    return totalProgressSum / totalStudents;
  }, [students, dimensionsTemplate, totalStudents]);

  const overallDataCompleteness = React.useMemo(() => {
    if (totalStudents === 0) return 0;
    const totalCompleteness = students.reduce((sum, student) => sum + calculateStudentDataCompleteness(student, dimensionsTemplate), 0);
    return totalCompleteness / totalStudents;
  }, [students, dimensionsTemplate, totalStudents]);

  // Estadísticas de estudiantes por categorías
  const studentsAtRisk = React.useMemo(() => 
    students.filter(s => calculateStudentOverallProgress(s, dimensionsTemplate) < 50).length, 
    [students, dimensionsTemplate]);
  
  const studentsExcellent = React.useMemo(() => 
    students.filter(s => calculateStudentOverallProgress(s, dimensionsTemplate) > 80).length, 
    [students, dimensionsTemplate]);
    
  const studentsMidRange = React.useMemo(() => 
    students.filter(s => {
      const progress = calculateStudentOverallProgress(s, dimensionsTemplate);
      return progress >= 50 && progress <= 80;
    }).length, 
    [students, dimensionsTemplate]);
  
  const getWelcomeMessage = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return "¡Buenos días";
    if (currentHour < 18) return "¡Buenas tardes";
    return "¡Buenas noches";
  };
  
  // ===== Metas del usuario (funcional, no mock) =====
  type GoalItem = {
    id: string;
    title: string;
    description: string;
    dueDate: string | null;
    priority: string;
    progress: number;
    targetValue: number;
    currentValue: number;
    status: string;
  };

  const [userGoals, setUserGoals] = React.useState<GoalItem[]>([]);
  const [updatingGoalId, setUpdatingGoalId] = React.useState<string | null>(null);

  // ❌ DESHABILITADO: User goals no funcional actualmente
  // React.useEffect(() => {
  //   (async () => {
  //     try {
  //       const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  //       const base = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';
  //       const resp = await fetch(`${base}/api/user-goals`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  //       const data = await resp.json();
  //       if (data.success) {
  //         const mapped: GoalItem[] = (data.goals || []).map((g: any) => ({
  //           id: String(g.id),
  //           title: g.title,
  //           description: g.description,
  //           dueDate: g.dueDate ? new Date(g.dueDate).toISOString() : null,
  //           priority: g.priority || 'medium',
  //           progress: typeof g.progress === 'number' ? g.progress : 0,
  //           targetValue: typeof g.targetValue === 'number' ? g.targetValue : 100,
  //           currentValue: typeof g.currentValue === 'number' ? g.currentValue : 0,
  //           status: g.status || 'pending',
  //         }));
  //         setUserGoals(mapped);
  //         console.log('[Dashboard] Metas cargadas:', mapped);
  //       }
  //     } catch (e) {
  //       // Silencioso en dashboard
  //     }
  //   })();
  // }, []);

  const updateGoalProgress = async (goal: GoalItem, percent: number, note?: string) => {
    try {
      setUpdatingGoalId(goal.id);
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const base = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';
      const currentValue = Math.round((goal.targetValue || 100) * (percent / 100));
      const resp = await fetch(`${base}/api/update-goal-progress/${goal.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ currentValue, notes: note || '', percent })
      });
      const data = await resp.json();
      if (data.success) {
        setUserGoals(prev => prev.map(g => g.id === goal.id ? { ...g, progress: percent, currentValue } : g));
      }
    } finally {
      setUpdatingGoalId(null);
    }
  };

  const upcomingGoals = React.useMemo(() => {
    const now = new Date();
    const in14 = new Date(now.getTime() + 14*24*60*60*1000);
    return userGoals
      .filter(g => g.dueDate && g.status !== 'completed')
      .filter(g => {
        const d = new Date(g.dueDate as string);
        return d >= now && d <= in14;
      })
      .sort((a, b) => new Date(a.dueDate as string).getTime() - new Date(b.dueDate as string).getTime())
      .slice(0, 6);
  }, [userGoals]);
  
  // Función para navegar a otras secciones
  const handleNavigateToSection = (section: 'formularios' | 'analytics' | 'usuarios' | 'exportacion') => {
    if (onSectionChange) {
      onSectionChange(section);
    }
  };

  // Funciones del tutorial removidas - ahora manejadas por SimpleTutorialSection
  
  return (
    <div className="p-6 max-w-full mx-auto space-y-8">
      {/* Header simplificado */}
      <div className="dashboard-stats bg-gradient-to-r from-gray-800 to-slate-900 rounded-2xl shadow-lg p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-3xl font-bold mb-1">{getWelcomeMessage()}, {username}! ({role === 'admin' ? 'Admin' : 'Teacher'})</h1>
             <p className="flex items-center text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              Fecha actual: {formatCurrentDate()}
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col md:items-end">
                <div className="flex items-center text-sm bg-white bg-opacity-20 px-3 py-1 rounded-full">
                 <span className="mr-2 bg-sky-400 rounded-full h-2 w-2"></span>
                 <span>{totalStudents} estudiantes en total</span>
            </div>
            <div className="mt-2 text-gray-300 text-sm">
                <div className="flex items-center space-x-4">
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                    </svg>
                    Progreso: {overallAverageProgress.toFixed(1)}%
                  </span>
                  <span className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Datos: {overallDataCompleteness.toFixed(1)}%
                  </span>
                </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* KPIs de Vista General */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* KPI 1: Total Estudiantes */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="p-5">
                <div className="flex justify-between">
                  <div>
                    <h3 className="text-gray-600 text-sm font-semibold uppercase tracking-wider">Total Estudiantes</h3>
                    <p className="text-4xl font-bold text-gray-800 mt-1">{totalStudents}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full self-start">
                    <FaUsers className="text-2xl text-blue-600" />
                  </div>
                </div>
                
                <div className="mt-5 space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                      <span className="text-sm text-gray-600">En riesgo:</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm font-semibold text-gray-800">{studentsAtRisk}</span>
                  <span className="text-xs text-gray-500 ml-1">({totalStudents > 0 ? ((studentsAtRisk / totalStudents) * 100).toFixed(1) : 0}%)</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                      <span className="text-sm text-gray-600">En desarrollo:</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm font-semibold text-gray-800">{studentsMidRange}</span>
                  <span className="text-xs text-gray-500 ml-1">({totalStudents > 0 ? ((studentsMidRange / totalStudents) * 100).toFixed(1) : 0}%)</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-sm text-gray-600">Destacados:</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm font-semibold text-gray-800">{studentsExcellent}</span>
                  <span className="text-xs text-gray-500 ml-1">({totalStudents > 0 ? ((studentsExcellent / totalStudents) * 100).toFixed(1) : 0}%)</span>
                    </div>
                  </div>
                </div>
                
            {totalStudents > 0 && (
                <div className="mt-4 bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div className="flex h-full">
                    <div 
                      className="bg-red-500 h-full" 
                      style={{width: `${(studentsAtRisk / totalStudents) * 100}%`}}
                    ></div>
                    <div 
                      className="bg-yellow-500 h-full" 
                      style={{width: `${(studentsMidRange / totalStudents) * 100}%`}}
                    ></div>
                    <div 
                      className="bg-green-500 h-full" 
                      style={{width: `${(studentsExcellent / totalStudents) * 100}%`}}
                    ></div>
                  </div>
                </div>
            )}
                  </div>
                </div>
                
        {/* KPI 2: Progreso Global */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="p-5">
                <div className="flex justify-between">
                  <div>
                    <h3 className="text-gray-600 text-sm font-semibold uppercase tracking-wider">Progreso Global</h3>
                    <p className="text-4xl font-bold text-gray-800 mt-1">{overallAverageProgress.toFixed(1)}%</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full self-start">
                    <FaChartLine className="text-2xl text-green-600" />
                  </div>
                </div>
                
                <div className="mt-4 flex items-center">
                  <div className="w-full bg-gray-200 h-2 rounded-full mr-2">
                    <div 
                      className={`h-full rounded-full ${overallAverageProgress < 50 ? 'bg-red-500' : overallAverageProgress < 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                      style={{width: `${overallAverageProgress}%`}}
                    ></div>
                  </div>
                  <div className="text-xs font-medium text-gray-500">Meta: 70%</div>
                </div>
              </div>
            </div>
          
        {/* KPI 3: Ingreso de Datos */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="p-5">
                <div className="flex justify-between">
                  <div>
                    <h3 className="text-gray-600 text-sm font-semibold uppercase tracking-wider">Ingreso de Datos</h3>
                    <p className="text-4xl font-bold text-gray-800 mt-1">{overallDataCompleteness.toFixed(1)}%</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full self-start">
                    <FaClipboardList className="text-2xl text-blue-600" />
                  </div>
                </div>
                
            <div className="mt-4 flex items-center">
              <div className="w-full bg-gray-200 h-2 rounded-full mr-2">
                <div 
                  className={`h-full rounded-full ${overallDataCompleteness < 70 ? 'bg-red-500' : overallDataCompleteness < 90 ? 'bg-amber-500' : 'bg-green-500'}`}
                  style={{width: `${overallDataCompleteness}%`}}
                ></div>
                    </div>
              <div className="text-xs font-medium text-gray-500">Meta: 90%</div>
            </div>
                    </div>
                  </div>
                  
        {/* KPI 4: Estado del Sistema */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
          <div className="p-5">
            <div className="flex justify-between">
              <div>
                <h3 className="text-gray-600 text-sm font-semibold uppercase tracking-wider">Estado del Sistema</h3>
                <div className="text-lg font-bold text-green-600 mt-1 flex items-center">
                  <div className="w-3 h-3 rounded-full bg-green-400 mr-2 animate-pulse"></div>
                  Operativo
                </div>
                      </div>
              <div className="bg-purple-100 p-3 rounded-full self-start">
                <FaRocket className="text-2xl text-purple-600" />
                      </div>
                    </div>
            
            <div className="mt-5 space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Conexión:</span>
                <span className="text-green-600 font-medium">Estable</span>
                    </div>
              <div className="flex justify-between">
                <span>Última sincronización:</span>
                <span className="text-gray-800 font-medium">Ahora</span>
                    </div>
                  </div>
                    </div>
                  </div>
                </div>
                
      {/* Botones de navegación rápida */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Acciones Rápidas</h2>
          {/* Botón Tutorial - ahora usa la función global */}
          <button
            onClick={() => {
              if (window.startTutorial) {
                window.startTutorial();
              } else {
                console.warn('Tutorial no disponible');
              }
            }}
            className="flex items-center space-x-2 bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600 text-white px-4 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
            title="¿Primera vez? ¡Inicia el tutorial!"
          >
            <FaLightbulb className="text-white" />
            <span className="text-sm font-medium">Iniciar Tutorial</span>
          </button>
        </div>
        <div className={`grid grid-cols-1 ${role === 'admin' ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
          {/* Botón para ir a Formularios */}
          <button 
            onClick={() => handleNavigateToSection('formularios')}
            className="group bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white p-6 rounded-lg transition-all duration-200 transform hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-2">Ingresar Datos</h3>
                <p className="text-blue-100 text-sm">Evaluar estudiantes y completar formularios</p>
                  </div>
              <div className="text-blue-200 group-hover:text-white transition-colors">
                <FaPen size={24} />
                </div>
              </div>
            <div className="flex items-center mt-4 text-blue-100">
              <span className="text-sm">Ir a Formularios</span>
              <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={12} />
            </div>
          </button>

          {/* Botón para ir a Analytics */}
          <button 
            onClick={() => handleNavigateToSection('analytics')}
            className="group bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white p-6 rounded-lg transition-all duration-200 transform hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-2">Analytics Avanzado</h3>
                <p className="text-indigo-100 text-sm">Análisis histórico, vistas individuales y reportes</p>
                  </div>
              <div className="text-indigo-200 group-hover:text-white transition-colors">
                <FaChartBar size={24} />
                </div>
            </div>
            <div className="flex items-center mt-4 text-indigo-100">
              <span className="text-sm">Ir a Analytics</span>
              <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={12} />
          </div>
          </button>

          {/* Botón para ir a Exportación */}
          <button 
            onClick={() => handleNavigateToSection('exportacion')}
            className="group bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white p-6 rounded-lg transition-all duration-200 transform hover:-translate-y-1 hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-2">Exportar Datos</h3>
                <p className="text-green-100 text-sm">Generar reportes y descargar información</p>
              </div>
              <div className="text-green-200 group-hover:text-white transition-colors">
                <FaFileExcel size={24} />
              </div>
            </div>
            <div className="flex items-center mt-4 text-green-100">
              <span className="text-sm">Ir a Exportación</span>
              <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={12} />
            </div>
          </button>

          {/* Botón para ir a Configuración (solo admin) */}
          {role === 'admin' && (
            <button 
              onClick={() => handleNavigateToSection('usuarios')}
              className="group bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white p-6 rounded-lg transition-all duration-200 transform hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">Configuración</h3>
                  <p className="text-purple-100 text-sm">Usuarios, backup y dimensiones 2025</p>
          </div>
                <div className="text-purple-200 group-hover:text-white transition-colors">
                  <FaUsersCog size={24} />
          </div>
              </div>
              <div className="flex items-center mt-4 text-purple-100">
                <span className="text-sm">Ir a Configuración</span>
                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={12} />
              </div>
            </button>
          )}
        </div>
        
        {/* Metas Próximas a Vencer - Bajo las acciones rápidas */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <FaBullseye className="mr-2 text-blue-600" />
              Metas Próximas a Vencer
            </h3>
            <div className="flex items-center text-sm text-gray-500">
              <FaRegClock className="mr-1" />
              <span>Próximos 7 días</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upcomingGoals.length === 0 ? (
              <div className="text-gray-500">No hay metas próximas a vencer</div>
            ) : (
              upcomingGoals.map(goal => {
                const due = goal.dueDate ? new Date(goal.dueDate).toLocaleDateString() : '—';
                return (
                  <div key={goal.id} className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div>
                        <h4 className="font-medium text-gray-900">{goal.title}</h4>
                        <p className="text-sm text-gray-600">Vence: {due}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${goal.progress}%` }}></div>
                      </div>
                      <button
                        className="text-xs px-3 py-1 bg-green-600 text-white rounded"
                        disabled={updatingGoalId === goal.id}
                        onClick={() => updateGoalProgress(goal, 100, 'Completa desde dashboard')}
                      >
                        Marcar completada
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          {/* Mostrar mensaje si no hay metas urgentes */}
          {overallDataCompleteness >= 90 && studentsAtRisk === 0 && (
            <div className="mt-4 text-center">
              <span className="text-sm text-gray-500">¡Excelente trabajo! No hay metas urgentes pendientes.</span>
            </div>
          )}
        </div>
      </div>





      {/* Tutorial ahora manejado por SimpleTutorialSection en App.tsx */}
    </div>
  );
};

export default Dashboard;
